import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import StatusPill from "@/components/StatusPill";
import api from "@/lib/api";
import { formatCurrency, formatDate, formatPeriod, currentPeriod, periodOptions } from "@/lib/format";
import { Link } from "react-router-dom";
import { Building2, Users, FileText, TrendingUp, ArrowRight, Wallet, Receipt, Percent, PiggyBank } from "lucide-react";

const Metric = ({ label, value, sub, icon: Icon, tone = "default", testid }) => (
  <div className="card-flat p-6 fade-up" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${
        tone === "green" ? "bg-emerald-50 text-emerald-700" :
        tone === "amber" ? "bg-amber-50 text-amber-700" :
        tone === "indigo" ? "bg-indigo-50 text-indigo-700" :
        "bg-slate-100 text-slate-700"
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
    </div>
    <div className="font-display text-3xl font-semibold tracking-tight">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: d } = await api.get(`/dashboard?period=${period}`);
      setData(d);
    })();
  }, [period]);

  useEffect(() => {
    (async () => {
      const { data: q } = await api.get("/reminders/queue");
      setQueue(q);
    })();
  }, []);

  return (
    <Layout
      title="Dashboard"
      subtitle={`Rent roll snapshot for ${formatPeriod(period)}`}
      actions={
        <select
          value={period} onChange={(e) => setPeriod(e.target.value)}
          data-testid="dashboard-period-select"
          className="h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
        >
          {periodOptions().map((p) => <option key={p} value={p}>{formatPeriod(p)}</option>)}
        </select>
      }
    >
      {/* Financial metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        <Metric label="Gross rental" value={formatCurrency(data?.gross_rental)} sub="Sum of all monthly rents" icon={Wallet} testid="metric-gross" />
        <Metric label="Agent commission" value={formatCurrency(data?.agent_commission)} sub="Deducted from gross" icon={Percent} tone="amber" testid="metric-commission" />
        <Metric label="Taxes" value={formatCurrency(data?.taxes)} sub="Applied to gross" icon={Receipt} tone="indigo" testid="metric-taxes" />
        <Metric label="Nett rent" value={formatCurrency(data?.nett_rent)} sub="Gross − commission − taxes" icon={PiggyBank} tone="green" testid="metric-nett" />
      </div>

      {/* Collection status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card-flat p-6 fade-up" data-testid="metric-collected">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Collected</div>
          <div className="font-display text-2xl font-semibold text-emerald-700">{formatCurrency(data?.collected)}</div>
          <div className="text-xs text-slate-500 mt-2">{data?.counts?.paid_count || 0} payments received</div>
        </div>
        <div className="card-flat p-6 fade-up" data-testid="metric-outstanding">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Outstanding</div>
          <div className="font-display text-2xl font-semibold text-red-700">{formatCurrency(data?.outstanding)}</div>
          <div className="text-xs text-slate-500 mt-2">{data?.counts?.unpaid_count || 0} payments pending</div>
        </div>
        <div className="card-flat p-6 fade-up">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Portfolio</div>
          <div className="grid grid-cols-3 gap-3 mt-2">
            <div>
              <div className="font-display text-xl font-semibold" data-testid="count-properties">{data?.counts?.properties ?? 0}</div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Building2 className="w-3 h-3" />Properties</div>
            </div>
            <div>
              <div className="font-display text-xl font-semibold" data-testid="count-tenants">{data?.counts?.tenants ?? 0}</div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Users className="w-3 h-3" />Tenants</div>
            </div>
            <div>
              <div className="font-display text-xl font-semibold" data-testid="count-contracts">{data?.counts?.active_contracts ?? 0}</div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><FileText className="w-3 h-3" />Active</div>
            </div>
          </div>
        </div>
      </div>

      {/* Reminders queue preview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <QueueCard
          title="Overdue payments"
          count={queue?.overdue?.length || 0}
          empty="No overdue payments — nicely done."
          items={(queue?.overdue || []).slice(0, 4).map((o) => ({
            key: o.id,
            primary: o.tenant?.full_name || "—",
            secondary: `${formatPeriod(o.period)} · ${formatCurrency(o.gross)}`,
            pill: "overdue",
          }))}
          testid="queue-overdue"
        />
        <QueueCard
          title="Upcoming increases"
          count={queue?.upcoming_increases?.length || 0}
          empty="No rent increases scheduled soon."
          items={(queue?.upcoming_increases || []).slice(0, 4).map((c) => ({
            key: c.id,
            primary: c.tenant?.full_name || "—",
            secondary: `Next: ${formatDate(c.next_increase_date)} · +${c.increase_percent || 0}%`,
            pill: "upcoming_increase",
          }))}
          testid="queue-increases"
        />
        <QueueCard
          title="Expiring contracts"
          count={queue?.expiring_contracts?.length || 0}
          empty="No contracts expiring in the next 60 days."
          items={(queue?.expiring_contracts || []).slice(0, 4).map((c) => ({
            key: c.id,
            primary: c.tenant?.full_name || "—",
            secondary: `Ends: ${formatDate(c.end_date)}`,
            pill: "due_soon",
          }))}
          testid="queue-expiring"
        />
      </div>

      {/* Empty state helper */}
      {(!data?.counts?.properties && !data?.counts?.tenants) && (
        <div className="mt-8 card-flat p-8 text-center fade-up">
          <div className="font-display text-lg font-semibold">Let&apos;s set up your first property</div>
          <p className="text-sm text-slate-500 mt-1 mb-4">Add a property, a tenant, and a contract — the rent roll will be waiting.</p>
          <div className="flex gap-2 justify-center">
            <Link to="/" data-testid="cta-add-property" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors">
              Add property <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/tenants" data-testid="cta-add-tenant" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-100 transition-colors">
              Add tenant
            </Link>
          </div>
        </div>
      )}
    </Layout>
  );
}

function QueueCard({ title, count, empty, items, testid }) {
  return (
    <div className="card-flat p-6 fade-up" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-semibold">{title}</div>
        <span className="pill pill-muted">{count}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 py-4">{empty}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it) => (
            <li key={it.key} className="py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{it.primary}</div>
                <div className="text-xs text-slate-500 truncate">{it.secondary}</div>
              </div>
              <StatusPill status={it.pill} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
